import { ChangeDetectionStrategy, Component, computed, inject, Signal, signal, WritableSignal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { combineLatest, switchMap } from 'rxjs';
import { DateFormatPipe, PageLayoutComponent, TitileNavbarComponent } from '@ui-components';
import { DaysRangeSliderComponent } from '../days-range-slider/days-range-slider.component';
import { AppRoutes, CopyValueToClipboardDirective, GamesRoutes } from '@core';
import { AdminGameTypeFilter, GameVisibility, Visibility } from '@core-http';
import { Router } from '@angular/router';
import {
  AdminGameQueryResult,
  GetAdminGamesQueryHandler,
  GetKnownRegionsQueryHandler
} from '../../application/handlers';

@Component({
  selector: 'lib-games',
  templateUrl: './games.component.html',
  styleUrls: ['./games.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    DateFormatPipe,
    CopyValueToClipboardDirective,
    DaysRangeSliderComponent,
    TitileNavbarComponent,
    PageLayoutComponent
  ],
  providers: [GetAdminGamesQueryHandler, GetKnownRegionsQueryHandler]
})
export class GamesComponent {
  private readonly getAdminGamesQueryHandler: GetAdminGamesQueryHandler = inject(GetAdminGamesQueryHandler);
  private readonly getKnownRegionsQueryHandler: GetKnownRegionsQueryHandler = inject(GetKnownRegionsQueryHandler);
  private readonly router: Router = inject(Router);

  readonly Visibility: typeof Visibility = Visibility;
  readonly AdminGameTypeFilter: typeof AdminGameTypeFilter = AdminGameTypeFilter;
  readonly GameVisibility: typeof GameVisibility = GameVisibility;

  readonly gamesPeriod: WritableSignal<number> = signal<number>(7);
  readonly gameType: WritableSignal<AdminGameTypeFilter | undefined> = signal<AdminGameTypeFilter | undefined>(
    undefined
  );
  readonly visibility: WritableSignal<GameVisibility | undefined> = signal<GameVisibility | undefined>(undefined);
  readonly region: WritableSignal<string | undefined> = signal<string | undefined>(undefined);
  readonly regionsExpanded: WritableSignal<boolean> = signal<boolean>(false);

  readonly knownRegions: Signal<string[] | undefined> = toSignal(this.getKnownRegionsQueryHandler.query());

  readonly games: Signal<AdminGameQueryResult[] | undefined> = toSignal(
    combineLatest([
      toObservable(this.gamesPeriod),
      toObservable(this.gameType),
      toObservable(this.visibility),
      toObservable(this.region)
    ]).pipe(
      switchMap(
        ([gamesPeriod, gameType, visibility, region]: [
          number,
          AdminGameTypeFilter | undefined,
          GameVisibility | undefined,
          string | undefined
        ]) => this.getAdminGamesQueryHandler.query(gamesPeriod, gameType, visibility, region)
      )
    )
  );
  readonly gamesCount: Signal<number> = computed(() => this.games()?.length ?? 0);

  goToDetails(gameId: string): void {
    this.router.navigate([`${AppRoutes.GAMES}/${GamesRoutes.DETAILS}/${gameId}`]);
  }

  setPeriod(period: number): void {
    this.gamesPeriod.set(period);
  }

  setGameType(type: AdminGameTypeFilter): void {
    this.gameType.set(this.gameType() === type ? undefined : type);
  }

  setVisibility(vis: GameVisibility): void {
    this.visibility.set(this.visibility() === vis ? undefined : vis);
  }

  setRegion(region: string): void {
    this.region.set(this.region() === region ? undefined : region);
  }
}
